/**
 * The catalogue editor's forms (Sản phẩm & giá › Danh mục sản phẩm, ADR-026 §6): groups, products
 * and whether they are shown, plans and their specifications, configuration choices with their
 * conditions and items, add-ons, the prices of options and add-ons, cross-sells, free domains,
 * ending a plan price, another location, back to the group's overdue settings, locations and
 * server groups. Each form is one audited write with a reason (`useWrite`, one Idempotency-Key per
 * request); the pages show a form only to who holds the permission the route checks.
 */
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '../../api';
import type {
  Branch,
  CatalogAddon,
  CatalogGroup,
  CatalogPlan,
  CatalogProduct,
  CycleCode,
  ExtraPriceRow,
  FreeDomainInput,
  Location,
  OptionCondition,
  OptionGroup,
  OptionItem,
  OptionItemVisibility,
  OptionKind,
  PlacementGroup,
  PlanDetail,
  PriceBookRef,
  SaleMode,
  StockMode,
  Visibility,
} from '../../api/areas/catalog';
import type { ModuleManifest } from '../../api/areas/modules';
import { Callout } from '../../components/blocks';
import { useWrite } from '../../components/Write';
import { useApi } from '../../lib/useApi';
import {
  ADDON_BILLINGS,
  ADDON_FULFILMENTS,
  BRANCHES,
  codeOk,
  conditionProblems,
  CONDITION_EFFECTS,
  cyclesProblem,
  CYCLES,
  formatVnd,
  FREE_DOMAIN_OPERATIONS,
  FULFILMENTS,
  groupCodeOk,
  ITEM_VISIBILITIES,
  KINDS_WITH_ITEMS,
  OPTION_KEYS,
  OPTION_KINDS,
  optionCodeOk,
  optionKindOf,
  parseVnd,
  percentValue,
  SALE_MODES,
  SERVICE_LABELS,
  sortCycles,
  specsOut,
  STOCK_MODES,
  STRATEGIES,
  VISIBILITIES,
} from '../../lib/catalog';
import { formatDateTime } from '../../lib/format';
import type { FormValue } from '../modules/fieldKinds';
import { useText } from '../modules/ui';
import { selectClass, type DialogBodyProps } from '../operator/shared';
import { CatalogCheck, CatalogField, CheckList, CodeInput, FormShell, NameFields, optionalInt, useCatalogT, useSuggestedCode } from './editorKit';
import { memberKeys, PlacementFields, placementPayload, placementValues, useModuleSummaries, usePlanModules } from './moduleKit';

const iso = (local: string): string | null => (local ? new Date(local).toISOString() : null);

/**
 * The tax codes a group, a plan or an add-on may take: the VAT codes with a rate in force
 * (Pricing → Tax rates), plus KCT (not taxable), plus the current code if it no longer has a rate
 * (shown so it can be changed).
 */
function useTaxCodes(current?: string) {
  const r = useApi((signal) => api.listTaxCodes({ signal }), []);
  const codes = r.status === 'success' ? r.data.codes : [];
  const known = [...codes.map((c) => c.tax_code), 'KCT'];
  const options = current && !known.includes(current) ? [...known, current] : known;
  const preferred = (r.status === 'success' ? r.data.default : null) ?? codes[0]?.tax_code ?? null;
  const label = (code: string, kct: string, missing: string) => {
    if (code === 'KCT') return kct;
    const rate = codes.find((c) => c.tax_code === code)?.rate;
    return rate === undefined ? `${code} · ${missing}` : `${code} · ${Number(rate) * 100}%`;
  };
  return { options, preferred, label, loaded: r.status !== 'loading' };
}

/**
 * The VAT code select. A new add-on starts on the default VAT code once the codes are known (owner
 * decision 2026-10-05), never on a code with no rate. With `empty`, the select also offers no code
 * (`empty` names what that means: "Chưa đặt" for a group, "Theo nhóm (…)" for a plan) and picks
 * nothing by itself: a default would override the group's code (ADR-032 §2.11).
 */
function TaxCodeSelect({ value, onChange, current, empty }: { value: string; onChange: (v: string) => void; current?: string; empty?: string }) {
  const { t } = useCatalogT();
  const codes = useTaxCodes(current);
  useEffect(() => {
    if (empty === undefined && !value && codes.preferred) onChange(codes.preferred);
  }, [empty, value, codes.preferred, onChange]);
  return (
    <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value)} required={empty === undefined}>
      {empty !== undefined ? <option value="">{empty}</option> : value ? null : <option value="">{codes.loaded ? t('editor.noTaxRate') : '…'}</option>}
      {codes.options.map((c) => (
        <option key={c} value={c}>
          {codes.label(c, t('tax.KCT'), t('editor.taxRateMissing'))}
        </option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------------------------
// Groups

export function GroupForm({ close, changed, group }: DialogBodyProps & { group?: CatalogGroup }) {
  const { t } = useCatalogT();
  const { code, type: setCode, fromName } = useSuggestedCode(group?.code, 40);
  const [vi, setVi] = useState(group?.name_vi ?? '');
  const [enName, setEn] = useState(group?.name_en ?? '');
  const [dVi, setDVi] = useState(group?.description_vi ?? '');
  const [dEn, setDEn] = useState(group?.description_en ?? '');
  const [sort, setSort] = useState(String(group?.sort ?? 100));
  const [saleMode, setSaleMode] = useState<SaleMode>(group?.sale_mode ?? 'CYCLE');
  const [tax, setTax] = useState(group?.tax_code ?? '');
  // ADR-032 §2.2: the sale mode is fixed once the group has a product (the server and the database
  // refuse it too); a locked mode is not sent, so the stored one stays (§4.2).
  const modeLocked = (group?.product_count ?? 0) > 0;
  type Body = Parameters<typeof api.createCatalogGroup>[0];
  const w = useWrite((b: Body, opts) => (group ? api.updateCatalogGroup(group.code, b, opts) : api.createCatalogGroup(b, opts)));
  const valid = Boolean(group) || groupCodeOk(code);
  return (
    <FormShell
      write={w}
      close={close}
      disabled={!valid || !vi.trim()}
      onSubmit={(reason) =>
        void w
          .run({
            code: group ? undefined : code,
            version: group?.version,
            name_vi: vi.trim(),
            name_en: (enName || vi).trim(),
            description_vi: dVi.trim() || null,
            description_en: dEn.trim() || null,
            sale_mode: modeLocked ? undefined : saleMode,
            tax_code: tax || null,
            sort: Number(sort || '100'),
            reason,
          })
          .then((r) => r && changed())
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <NameFields vi={vi} en={enName} setVi={(v) => { setVi(v); fromName(v); }} setEn={setEn} />
        <CatalogField name="code">
          <CodeInput value={code} onChange={setCode} locked={Boolean(group)} valid={valid} />
        </CatalogField>
        <CatalogField name="saleMode">
          <select className={selectClass} value={saleMode} disabled={modeLocked} onChange={(e) => setSaleMode(e.target.value as SaleMode)}>
            {SALE_MODES.map((m) => (
              <option key={m} value={m}>
                {t(`saleMode.${m}`)}
              </option>
            ))}
          </select>
          {modeLocked ? <span className="text-xs text-muted-foreground">{t('editor.saleModeLocked')}</span> : null}
        </CatalogField>
        <CatalogField name="groupTax">
          <TaxCodeSelect value={tax} onChange={setTax} current={group?.tax_code ?? undefined} empty={t('editor.groupTaxNone')} />
        </CatalogField>
        <CatalogField name="sort">
          <Input type="number" value={sort} onChange={(e) => setSort(e.target.value)} className="w-28" />
        </CatalogField>
        <CatalogField name="descriptionVi">
          <Textarea value={dVi} onChange={(e) => setDVi(e.target.value)} rows={2} />
        </CatalogField>
        <CatalogField name="descriptionEn">
          <Textarea value={dEn} onChange={(e) => setDEn(e.target.value)} rows={2} />
        </CatalogField>
      </div>
    </FormShell>
  );
}

// ---------------------------------------------------------------------------------------------
// Products

export function ProductForm({ close, changed, product, groups, defaultGroup, hasPlans }: DialogBodyProps & { product?: CatalogProduct; groups: readonly CatalogGroup[]; defaultGroup?: string | null; hasPlans?: boolean }) {
  const { t, name } = useCatalogT();
  const { code, type: setCode, fromName } = useSuggestedCode(product?.code, 60);
  const [group, setGroup] = useState(product?.group_code ?? defaultGroup ?? groups[0]?.code ?? '');
  const [vi, setVi] = useState(product?.name_vi ?? '');
  const [enName, setEn] = useState(product?.name_en ?? '');
  const [dVi, setDVi] = useState(product?.description_vi ?? '');
  const [dEn, setDEn] = useState(product?.description_en ?? '');
  const [fulfilment, setFulfilment] = useState<CatalogProduct['fulfilment']>(product?.fulfilment ?? 'PROVISIONED');
  const [branch, setBranch] = useState<Branch>(product?.branch ?? 'hosting');
  const [label, setLabel] = useState<CatalogProduct['service_label']>(product?.service_label ?? 'DOMAIN');
  const [trial, setTrial] = useState(product?.trial_days?.toString() ?? '');
  const [moneyBack, setMoneyBack] = useState(product?.money_back_days?.toString() ?? '');
  const [sort, setSort] = useState(String(product?.sort ?? 100));
  type Body = Parameters<typeof api.createCatalogProduct>[0];
  const w = useWrite((b: Body, opts) => (product ? api.updateCatalogProduct(product.id, b, opts) : api.createCatalogProduct(b, opts)));
  const valid = Boolean(product) || codeOk(code);
  const deliveryLocked = Boolean(product && hasPlans);
  return (
    <FormShell
      write={w}
      close={close}
      disabled={!valid || !vi.trim() || !group}
      onSubmit={(reason) =>
        void w
          .run({
            code: product ? undefined : code,
            version: product?.version,
            group_code: group,
            name_vi: vi.trim(),
            name_en: (enName || vi).trim(),
            description_vi: dVi.trim() || null,
            description_en: dEn.trim() || null,
            fulfilment,
            branch: fulfilment === 'PROVISIONED' ? branch : null,
            service_label: label,
            trial_days: optionalInt(trial),
            money_back_days: optionalInt(moneyBack),
            sort: Number(sort || '100'),
            reason,
          })
          .then((r) => r && changed())
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <NameFields vi={vi} en={enName} setVi={(v) => { setVi(v); fromName(v); }} setEn={setEn} />
        <CatalogField name="code">
          <CodeInput value={code} onChange={setCode} locked={Boolean(product)} valid={valid} />
        </CatalogField>
        <CatalogField name="group">
          <select className={selectClass} value={group} onChange={(e) => setGroup(e.target.value)}>
            {groups.map((g) => (
              <option key={g.code} value={g.code}>
                {name(g)}
              </option>
            ))}
          </select>
        </CatalogField>
        <CatalogField name="fulfilment">
          <select className={selectClass} value={fulfilment} disabled={deliveryLocked} onChange={(e) => setFulfilment(e.target.value as CatalogProduct['fulfilment'])}>
            {FULFILMENTS.map((f) => (
              <option key={f} value={f}>
                {t(`fulfilment.${f}`)}
              </option>
            ))}
          </select>
        </CatalogField>
        {fulfilment === 'PROVISIONED' ? (
          <CatalogField name="branch">
            <select className={selectClass} value={branch} disabled={deliveryLocked} onChange={(e) => setBranch(e.target.value as Branch)}>
              {BRANCHES.map((b) => (
                <option key={b} value={b}>
                  {t(`branch.${b}`)}
                </option>
              ))}
            </select>
          </CatalogField>
        ) : null}
        <CatalogField name="serviceLabel">
          <select className={selectClass} value={label} onChange={(e) => setLabel(e.target.value as CatalogProduct['service_label'])}>
            {SERVICE_LABELS.map((l) => (
              <option key={l} value={l}>
                {t(`serviceLabel.${l}`)}
              </option>
            ))}
          </select>
        </CatalogField>
        <CatalogField name="trialDays">
          <Input type="number" min={1} max={30} value={trial} onChange={(e) => setTrial(e.target.value)} className="w-28" />
        </CatalogField>
        <CatalogField name="moneyBackDays">
          <Input type="number" min={1} max={90} value={moneyBack} onChange={(e) => setMoneyBack(e.target.value)} className="w-28" />
        </CatalogField>
        <CatalogField name="sort">
          <Input type="number" value={sort} onChange={(e) => setSort(e.target.value)} className="w-28" />
        </CatalogField>
        <CatalogField name="descriptionVi">
          <Textarea value={dVi} onChange={(e) => setDVi(e.target.value)} rows={2} />
        </CatalogField>
        <CatalogField name="descriptionEn">
          <Textarea value={dEn} onChange={(e) => setDEn(e.target.value)} rows={2} />
        </CatalogField>
      </div>
    </FormShell>
  );
}

/** Show, hide or retire a product (`catalog.publish`). */
export function ProductVisibilityForm({ close, changed, product }: DialogBodyProps & { product: CatalogProduct }) {
  const { t } = useCatalogT();
  const [visibility, setVisibility] = useState<Visibility>(product.visibility === 'PUBLIC' ? 'HIDDEN' : 'PUBLIC');
  const w = useWrite((b: { visibility: Visibility; reason: string }, opts) => api.setProductVisibility(product.id, { ...b, version: product.version }, opts));
  return (
    <FormShell write={w} close={close} disabled={visibility === product.visibility} onSubmit={(reason) => void w.run({ visibility, reason }).then((r) => r && changed())}>
      <p className="text-sm text-muted-foreground">{t('editor.productVisibilityIntro')}</p>
      <CatalogField name="productVisibility">
        <select className={selectClass} value={visibility} onChange={(e) => setVisibility(e.target.value as Visibility)}>
          {VISIBILITIES.map((v) => (
            <option key={v} value={v}>
              {t(`visibility.${v}`)}
            </option>
          ))}
        </select>
      </CatalogField>
    </FormShell>
  );
}

/**
 * The product's "Tên miền" tab (ADR-032 §2.13, HostBill Components › Domains): what names a sold
 * service and the domain options. The whole product is sent, as "Sửa sản phẩm" does; the options
 * are the closed set of catalog_core::DomainOptions.
 */
export function DomainOptionsForm({ close, changed, product }: DialogBodyProps & { product: CatalogProduct }) {
  const { t } = useCatalogT();
  const o = product.domain_options;
  const [label, setLabel] = useState<CatalogProduct['service_label']>(product.service_label);
  const [register, setRegister] = useState(o.register);
  const [transfer, setTransfer] = useState(o.transfer);
  const [tlds, setTlds] = useState(o.tlds.join(', '));
  const [own, setOwn] = useState(o.own);
  const [here, setHere] = useState(o.own_registered_here_only);
  const [subdomain, setSubdomain] = useState(o.subdomain ?? '');
  const w = useWrite((b: Parameters<typeof api.updateCatalogProduct>[1], opts) => api.updateCatalogProduct(product.id, b, opts));
  return (
    <FormShell
      write={w}
      close={close}
      onSubmit={(reason) =>
        void w
          .run({
            version: product.version,
            group_code: product.group_code,
            name_vi: product.name_vi,
            name_en: product.name_en,
            description_vi: product.description_vi,
            description_en: product.description_en,
            fulfilment: product.fulfilment,
            branch: product.branch,
            service_label: label,
            trial_days: product.trial_days,
            money_back_days: product.money_back_days,
            sort: product.sort,
            domain_options: {
              register,
              transfer,
              // "vn, .com.vn  com" → ['vn', 'com.vn', 'com'].
              tlds: tlds.split(/[,\s]+/).map((x) => x.trim().replace(/^\./, '').toLowerCase()).filter(Boolean),
              own,
              own_registered_here_only: own && here,
              subdomain: subdomain.trim() || null,
            },
            reason,
          })
          .then((r) => r && changed())
      }
    >
      <CatalogField name="serviceLabel">
        <select className={selectClass} value={label} onChange={(e) => setLabel(e.target.value as CatalogProduct['service_label'])}>
          {SERVICE_LABELS.map((l) => (
            <option key={l} value={l}>
              {t(`serviceLabel.${l}`)}
            </option>
          ))}
        </select>
      </CatalogField>
      <CatalogCheck name="domainOwn" checked={own} onChange={setOwn} />
      <CatalogCheck name="domainOwnHere" checked={own && here} onChange={setHere} disabled={!own} />
      <CatalogCheck name="domainRegister" checked={register} onChange={setRegister} />
      <CatalogCheck name="domainTransfer" checked={transfer} onChange={setTransfer} />
      <CatalogField name="domainTlds">
        <Input value={tlds} onChange={(e) => setTlds(e.target.value)} className="font-mono" />
      </CatalogField>
      <CatalogField name="domainSubdomain">
        <Input value={subdomain} onChange={(e) => setSubdomain(e.target.value)} className="font-mono" placeholder=".tino.page" />
      </CatalogField>
    </FormShell>
  );
}

// ---------------------------------------------------------------------------------------------
// Plans

interface SpecRow {
  key?: string;
  label_vi: string;
  label_en: string;
  value: string;
  unit: string;
}

export function PlanForm({ close, changed, productId, group, plan, siblings }: DialogBodyProps & { productId: string; group: CatalogGroup | null; plan?: CatalogPlan; siblings: readonly { id: string; code: string; name_vi: string; name_en: string }[] }) {
  const { t, name } = useCatalogT();
  const { code, type: setCode, fromName } = useSuggestedCode(plan?.code, 60);
  const [vi, setVi] = useState(plan?.name_vi ?? '');
  const [enName, setEn] = useState(plan?.name_en ?? '');
  // ADR-032 §2.3: a plan of an hourly group is sold by the hour alone (H1), with no cycle to pick.
  const hourly = group?.sale_mode === 'HOURLY';
  const [cycles, setCycles] = useState<CycleCode[]>(plan?.cycles ?? (hourly ? ['H1'] : ['M1', 'M12']));
  const [tax, setTax] = useState(plan?.tax_code ?? '');
  const [upgrades, setUpgrades] = useState<string[]>(plan?.upgrades_to ?? []);
  const [sort, setSort] = useState(String(plan?.sort ?? 100));
  const [specs, setSpecs] = useState<SpecRow[]>(plan?.specs.map((s) => ({ key: s.key, label_vi: s.label_vi, label_en: s.label_en, value: s.value, unit: s.unit ?? '' })) ?? [{ label_vi: '', label_en: '', value: '', unit: '' }]);
  type Body = Parameters<typeof api.createCatalogPlan>[0];
  const w = useWrite((b: Body, opts) => (plan ? api.updateCatalogPlan(plan.id, b, opts) : api.createCatalogPlan(b, opts)));
  const valid = Boolean(plan) || codeOk(code);
  const cycleProblem = hourly ? null : cyclesProblem(cycles);
  const others = siblings.filter((s) => s.id !== plan?.id);
  const setSpec = (i: number, patch: Partial<SpecRow>) => setSpecs(specs.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  return (
    <FormShell
      write={w}
      close={close}
      disabled={!valid || !vi.trim() || cycleProblem !== null}
      onSubmit={(reason) =>
        void w
          .run({
            product_id: plan ? undefined : productId,
            code: plan ? undefined : code,
            version: plan?.version,
            name_vi: vi.trim(),
            name_en: (enName || vi).trim(),
            specs: specsOut(specs),
            cycles: hourly ? ['H1'] : sortCycles(cycles),
            // ADR-032 §2.11: empty is "theo nhóm", sent as null.
            tax_code: tax || null,
            upgrades_to: upgrades,
            sort: Number(sort || '100'),
            reason,
          })
          .then((r) => r && changed())
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <NameFields vi={vi} en={enName} setVi={(v) => { setVi(v); fromName(v); }} setEn={setEn} />
        <CatalogField name="code">
          <CodeInput value={code} onChange={setCode} locked={Boolean(plan)} valid={valid} />
        </CatalogField>
        <CatalogField name="planTax">
          <TaxCodeSelect
            value={tax}
            onChange={setTax}
            current={plan?.tax_code ?? undefined}
            empty={t('editor.taxFromGroup', { tax: group?.tax_code ? t(`tax.${group.tax_code}`, { defaultValue: group.tax_code }) : t('editor.groupTaxNone') })}
          />
        </CatalogField>
      </div>
      {hourly ? (
        <Callout tone="info">{t('editor.hourlyPlan')}</Callout>
      ) : (
        <CheckList name="cycles" options={CYCLES.filter((c) => c !== 'H1')} value={cycles} onChange={setCycles} label={(c) => t(`cycle.${c}`)} />
      )}
      {cycleProblem ? <Callout tone="warn">{t(`editor.cyclesProblem.${cycleProblem}`)}</Callout> : null}
      <CatalogField name="specs" group>
        <div className="grid gap-2">
          {specs.map((s, i) => (
            <div key={i} className="grid grid-cols-2 items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_6rem_auto]">
              <CatalogField name="specLabelVi">
                <Input value={s.label_vi} onChange={(e) => setSpec(i, { label_vi: e.target.value })} />
              </CatalogField>
              <CatalogField name="specLabelEn">
                <Input value={s.label_en} onChange={(e) => setSpec(i, { label_en: e.target.value })} />
              </CatalogField>
              <CatalogField name="specValue">
                <Input value={s.value} onChange={(e) => setSpec(i, { value: e.target.value })} />
              </CatalogField>
              <CatalogField name="specUnit">
                <Input value={s.unit} onChange={(e) => setSpec(i, { unit: e.target.value })} />
              </CatalogField>
              <Button type="button" size="sm" variant="ghost" onClick={() => setSpecs(specs.filter((_, k) => k !== i))} aria-label={t('editor.remove')}>
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" className="w-fit" onClick={() => setSpecs([...specs, { label_vi: '', label_en: '', value: '', unit: '' }])}>
            <Plus className="size-4" aria-hidden="true" />
            {t('editor.addSpec')}
          </Button>
        </div>
      </CatalogField>
      {others.length ? <CheckList name="upgradesTo" options={others.map((o) => o.id)} value={upgrades} onChange={setUpgrades} label={(id) => name(others.find((o) => o.id === id) ?? { name_vi: id, name_en: id })} /> : null}
      <CatalogField name="sort">
        <Input type="number" value={sort} onChange={(e) => setSort(e.target.value)} className="w-28" />
      </CatalogField>
    </FormShell>
  );
}

// ---------------------------------------------------------------------------------------------
// Configuration choices (option groups), their conditions and items

export function OptionGroupForm({ close, changed, productId, branch, group, siblings, plans }: DialogBodyProps & { productId: string; branch: Branch | null; group?: OptionGroup; siblings: readonly OptionGroup[]; plans: readonly CatalogPlan[] }) {
  const { t, name } = useCatalogT();
  const text = useText();
  const { code, type: setCode, fromName } = useSuggestedCode(group?.code, 40, true);
  const [vi, setVi] = useState(group?.name_vi ?? '');
  const [enName, setEn] = useState(group?.name_en ?? '');
  // ADR-032 §2.7: a standard kind, or `<module>:<key>`, an option type of a module the plans use;
  // the server takes such a type's kind and `maps_to` from its spec.
  const planModules = usePlanModules(branch, plans);
  const manifests = planModules ? [...new Set(planModules.values())].filter((m): m is ModuleManifest => m !== null) : null;
  const [type, setType] = useState<string>(group?.module_type ?? group?.kind ?? 'SELECT');
  const moduleType = type.includes(':') ? type : null;
  const spec = manifests?.flatMap((m) => m.option_types.filter((o) => `${m.id}:${o.key}` === type)).at(0) ?? null;
  const kind: OptionKind = moduleType ? (spec ? optionKindOf(spec.render) : (group?.kind ?? 'SELECT')) : (type as OptionKind);
  const [required, setRequired] = useState(group?.required ?? false);
  const [changeable, setChangeable] = useState(group?.changeable ?? false);
  const [minQty, setMinQty] = useState(group?.min_qty?.toString() ?? '0');
  const [maxQty, setMaxQty] = useState(group?.max_qty?.toString() ?? '10');
  const [step, setStep] = useState(group?.step?.toString() ?? '');
  const [mapsTo, setMapsTo] = useState(group?.maps_to ?? '');
  const [onPlans, setOnPlans] = useState<string[]>(group?.plans ?? []);
  // The targets `maps_to` may name (catalog_products::admin::option_target): the branch's option keys,
  // and each placement setting that every module of the option's plans (all plans when none is
  // ticked; plans without a module connection do not count) lets a plan override, once.
  const used = planModules ? [...new Set((onPlans.length ? onPlans : plans.map((p) => p.id)).filter((id) => planModules.has(id)).map((id) => planModules.get(id) ?? null))] : [];
  // A module the branch does not list (null) has no setting to check: none is offered.
  const mods = used.includes(null) ? [] : (used as ModuleManifest[]);
  const settings = (mods[0]?.placement_settings.fields ?? []).filter((f) => f.overridable && mods.every((m) => m.placement_settings.fields.some((x) => x.key === f.key && x.overridable)));
  const targets = [
    ...(branch ? OPTION_KEYS[branch] : []).map((k) => ({ value: k, label: t(`optionKey.${k}`, { defaultValue: k }) })),
    ...settings.map((f) => ({ value: `setting:${f.key}`, label: `${mods.map((m) => text(m.name)).join(', ')}: ${text(f.label)}` })),
  ];
  const [conditions, setConditions] = useState<OptionCondition[]>(group?.conditions ?? []);
  const [sort, setSort] = useState(String(group?.sort ?? 100));
  type Body = Parameters<typeof api.createOptionGroup>[1];
  const w = useWrite((b: Body, opts) => (group ? api.updateOptionGroup(group.id, b, opts) : api.createOptionGroup(productId, b, opts)));
  const others = siblings.filter((s) => s.id !== group?.id && s.items.length > 0);
  const valid = Boolean(group) || optionCodeOk(code);
  const quantity = kind === 'QUANTITY';
  const qtyProblem = quantity && (minQty === '' || maxQty === '' || Number(minQty) > Number(maxQty) || Number(minQty) < 0);
  const problems = conditionProblems(conditions, others);
  const ownItems = group?.items ?? [];
  const setCond = (i: number, patch: Partial<OptionCondition>) => setConditions(conditions.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  return (
    <FormShell
      write={w}
      close={close}
      disabled={!valid || !vi.trim() || qtyProblem || problems.length > 0}
      onSubmit={(reason) =>
        void w
          .run({
            code: group ? undefined : code,
            version: group?.version,
            name_vi: vi.trim(),
            name_en: (enName || vi).trim(),
            kind,
            // Set on creation; sent unchanged afterwards.
            module_type: group ? group.module_type : moduleType,
            required,
            changeable,
            min_qty: quantity ? Number(minQty) : null,
            max_qty: quantity ? Number(maxQty) : null,
            step: quantity ? optionalInt(step) : null,
            maps_to: moduleType ? null : mapsTo || null,
            plans: onPlans.length ? onPlans : null,
            conditions,
            sort: Number(sort || '100'),
            reason,
          })
          .then((r) => r && changed())
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <NameFields vi={vi} en={enName} setVi={(v) => { setVi(v); fromName(v); }} setEn={setEn} />
        <CatalogField name="optionCode">
          <CodeInput value={code} onChange={setCode} locked={Boolean(group)} valid={valid} />
        </CatalogField>
        <CatalogField name="optionKind">
          <select
            className={selectClass}
            value={type}
            disabled={Boolean(group)}
            onChange={(e) => {
              setType(e.target.value);
              const render = manifests?.flatMap((m) => m.option_types.filter((o) => `${m.id}:${o.key}` === e.target.value)).at(0)?.render;
              if (render?.kind === 'quantity') {
                setMinQty(String(render.min));
                setMaxQty(String(render.max));
              }
            }}
          >
            <optgroup label={t('options.standardKinds')}>
              {OPTION_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`options.kind.${k}`)}
                </option>
              ))}
            </optgroup>
            {(manifests ?? [])
              .filter((m) => m.option_types.length > 0)
              .map((m) => (
                <optgroup key={m.id} label={t('options.moduleKinds', { module: text(m.name) })}>
                  {m.option_types.map((o) => (
                    <option key={o.key} value={`${m.id}:${o.key}`}>
                      {text(o.label)}
                    </option>
                  ))}
                </optgroup>
              ))}
            {moduleType && !spec ? <option value={moduleType}>{moduleType}</option> : null}
          </select>
          {spec?.help ? <span className="text-xs text-muted-foreground">{text(spec.help)}</span> : null}
        </CatalogField>
        {quantity ? (
          <>
            <CatalogField name="minQty">
              <Input type="number" min={0} value={minQty} onChange={(e) => setMinQty(e.target.value)} className="w-28" />
            </CatalogField>
            <CatalogField name="maxQty">
              <Input type="number" min={0} value={maxQty} onChange={(e) => setMaxQty(e.target.value)} className="w-28" />
            </CatalogField>
            <CatalogField name="step">
              <Input type="number" min={1} value={step} onChange={(e) => setStep(e.target.value)} className="w-28" />
            </CatalogField>
          </>
        ) : null}
        {moduleType ? null : (
          <CatalogField name="mapsTo">
            <select className={selectClass} value={mapsTo} onChange={(e) => setMapsTo(e.target.value)}>
              <option value="">{t('options.mapsToNone')}</option>
              {mapsTo && !targets.some((x) => x.value === mapsTo) ? <option value={mapsTo}>{mapsTo}</option> : null}
              {targets.map((x) => (
                <option key={x.value} value={x.value}>
                  {x.label}
                </option>
              ))}
            </select>
          </CatalogField>
        )}
        <CatalogField name="sort">
          <Input type="number" value={sort} onChange={(e) => setSort(e.target.value)} className="w-28" />
        </CatalogField>
      </div>
      {qtyProblem ? <Callout tone="warn">{t('editor.quantityProblem')}</Callout> : null}
      <CatalogCheck name="required" checked={required} onChange={setRequired} />
      <CatalogCheck name="changeable" checked={changeable} onChange={setChangeable} />
      {plans.length > 1 ? <CheckList name="optionPlans" options={plans.map((p) => p.id)} value={onPlans} onChange={setOnPlans} label={(id) => name(plans.find((p) => p.id === id) ?? { name_vi: id, name_en: id })} /> : null}
      <CatalogField name="conditions" group>
        <div className="grid gap-3">
          {conditions.length === 0 ? <p className="text-xs text-muted-foreground">{t('editor.noConditions')}</p> : null}
          {conditions.map((c, i) => {
            const trigger = others.find((o) => o.code === c.when_group);
            return (
              <div key={i} className="grid gap-2 rounded-md border p-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <CatalogField name="whenGroup">
                    <select className={selectClass} value={c.when_group} onChange={(e) => setCond(i, { when_group: e.target.value, when_items: [] })}>
                      {others.map((o) => (
                        <option key={o.code} value={o.code}>
                          {name(o)}
                        </option>
                      ))}
                    </select>
                  </CatalogField>
                  <CatalogField name="effect">
                    <select className={selectClass} value={c.effect} onChange={(e) => setCond(i, { effect: e.target.value as OptionCondition['effect'] })}>
                      {CONDITION_EFFECTS.map((x) => (
                        <option key={x} value={x}>
                          {t(`options.effect.${x}`)}
                        </option>
                      ))}
                    </select>
                  </CatalogField>
                </div>
                {trigger ? <CheckList name="whenItems" options={trigger.items.map((x) => x.code)} value={c.when_items} onChange={(v) => setCond(i, { when_items: v })} label={(c2) => name(trigger.items.find((x) => x.code === c2) ?? { name_vi: c2, name_en: c2 })} /> : null}
                {ownItems.length ? <CheckList name="effectItems" options={ownItems.map((x) => x.code)} value={c.items} onChange={(v) => setCond(i, { items: v })} label={(c2) => name(ownItems.find((x) => x.code === c2) ?? { name_vi: c2, name_en: c2 })} /> : null}
                <Button type="button" size="sm" variant="ghost" className="w-fit" onClick={() => setConditions(conditions.filter((_, k) => k !== i))}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  {t('editor.remove')}
                </Button>
              </div>
            );
          })}
          {others.length ? (
            <Button type="button" size="sm" variant="outline" className="w-fit" onClick={() => setConditions([...conditions, { when_group: others[0]?.code ?? '', when_items: [], effect: 'HIDE', items: [] }])}>
              <Plus className="size-4" aria-hidden="true" />
              {t('editor.addCondition')}
            </Button>
          ) : null}
        </div>
      </CatalogField>
      {problems.length ? (
        <Callout tone="warn">
          {problems.map((p) => (
            <p key={p}>{t(`editor.conditionProblem.${p}`)}</p>
          ))}
        </Callout>
      ) : null}
    </FormShell>
  );
}

/** Kinds whose groups take items: the others ask for a number or a text. */
export const takesItems = (kind: OptionKind): boolean => KINDS_WITH_ITEMS.includes(kind);

export function OptionItemForm({ close, changed, group, item, branch }: DialogBodyProps & { group: OptionGroup; item?: OptionItem; branch: Branch | null }) {
  const { t, name } = useCatalogT();
  const { code, type: setCode, fromName } = useSuggestedCode(item?.code, 40, true);
  const [vi, setVi] = useState(item?.name_vi ?? '');
  const [enName, setEn] = useState(item?.name_en ?? '');
  const [value, setValue] = useState(item?.provider_value ?? '');
  const [placementGroup, setPlacementGroup] = useState(item?.placement_group ?? '');
  // ADR-032 §2.7: the values of a module's option type come as its spec says: a server group of the
  // same module, a value the servers list (typed here, checked on every server when saved), or typed.
  const [moduleId = '', typeKey] = group.module_type?.split(':') ?? [];
  const spec = useModuleSummaries(group.module_type ? branch : null)?.find((m) => m.manifest.id === moduleId)?.manifest.option_types.find((o) => o.key === typeKey) ?? null;
  // The group stores what its type does (`maps_to`), so this holds without the module's manifest.
  const toGroup = group.maps_to === 'placement_group';
  const groups = useApi((signal) => (toGroup ? api.listPlacementGroups({ signal }) : Promise.resolve(null)), [toGroup]);
  const sameModule = groups.status === 'success' && groups.data ? groups.data.placement_groups.filter((g) => g.module === moduleId && g.branch === branch) : [];
  const [isDefault, setDefault] = useState(item?.is_default ?? false);
  const [visibility, setVisibility] = useState<OptionItemVisibility>(item?.visibility ?? 'PUBLIC');
  const [sort, setSort] = useState(String(item?.sort ?? 100));
  type Body = Parameters<typeof api.createOptionItem>[1];
  const w = useWrite((b: Body, opts) => (item ? api.updateOptionItem(item.id, b, opts) : api.createOptionItem(group.id, b, opts)));
  const valid = Boolean(item) || optionCodeOk(code);
  return (
    <FormShell
      write={w}
      close={close}
      disabled={!valid || !vi.trim() || (toGroup && !placementGroup)}
      onSubmit={(reason) =>
        void w
          .run({
            code: item ? undefined : code,
            version: item?.version,
            name_vi: vi.trim(),
            name_en: (enName || vi).trim(),
            provider_value: toGroup ? null : value.trim() || null,
            placement_group: toGroup ? placementGroup : null,
            is_default: isDefault,
            visibility,
            sort: Number(sort || '100'),
            reason,
          })
          .then((r) => r && changed())
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <NameFields vi={vi} en={enName} setVi={(v) => { setVi(v); fromName(v); }} setEn={setEn} />
        <CatalogField name="optionCode">
          <CodeInput value={code} onChange={setCode} locked={Boolean(item)} valid={valid} />
        </CatalogField>
        {toGroup ? (
          <CatalogField name="itemPlacementGroup">
            <select className={selectClass} value={placementGroup} onChange={(e) => setPlacementGroup(e.target.value)} required>
              <option value="">{t('order.choose')}</option>
              {placementGroup && !sameModule.some((g) => g.code === placementGroup) ? <option value={placementGroup}>{placementGroup}</option> : null}
              {sameModule.map((g) => (
                <option key={g.code} value={g.code}>
                  {name(g)}
                </option>
              ))}
            </select>
          </CatalogField>
        ) : (
          <CatalogField name={spec?.values.kind === 'from_server' ? 'itemServerValue' : 'itemProviderValue'}>
            <Input value={value} onChange={(e) => setValue(e.target.value)} className="font-mono" />
          </CatalogField>
        )}
        <CatalogField name="itemVisibility">
          <select className={selectClass} value={visibility} onChange={(e) => setVisibility(e.target.value as OptionItemVisibility)}>
            {ITEM_VISIBILITIES.map((v) => (
              <option key={v} value={v}>
                {t(`editor.itemVisibility.${v}`)}
              </option>
            ))}
          </select>
        </CatalogField>
        <CatalogField name="sort">
          <Input type="number" value={sort} onChange={(e) => setSort(e.target.value)} className="w-28" />
        </CatalogField>
      </div>
      <CatalogCheck name="itemDefault" checked={isDefault} onChange={setDefault} />
    </FormShell>
  );
}

// ---------------------------------------------------------------------------------------------
// Prices of an option item or an add-on

/**
 * New prices of an option item or an add-on, per cycle, in one book (`pricing.manage`). An option
 * of an hourly plan has one `H1` row: a percent of the plan's hourly price and no amount (ADR-032
 * §2.3); an add-on has no `H1` row (its caller leaves `H1` out).
 */
export function ExtraPriceForm({ close, changed, target, kind, cycles, books, current, quantity }: DialogBodyProps & { target: string; kind: 'option' | 'addon'; cycles: readonly CycleCode[]; books: readonly PriceBookRef[]; current: readonly ExtraPriceRow[]; quantity?: boolean }) {
  const { t } = useCatalogT();
  const blank = { amount: '', setup: '', up: '', down: '', pct: '' };
  const [book, setBook] = useState(books[0]?.id ?? '');
  const [rows, setRows] = useState<Record<string, typeof blank>>({});
  const [from, setFrom] = useState('');
  const option = kind === 'option';
  const typed = sortCycles(cycles)
    .filter((c) => (c === 'H1' ? (rows[c]?.pct ?? '') : (rows[c]?.amount ?? '')).trim() !== '')
    .map((c) => {
      const r = rows[c] ?? blank;
      const v = (x: string) => (x.trim() ? parseVnd(x) : '0');
      return c === 'H1'
        ? { cycle: c, amount: '0', setup_fee: '0', upgrade_fee: '0', downgrade_fee: '0', percent: percentValue(r.pct) }
        : { cycle: c, amount: parseVnd(r.amount), setup_fee: v(r.setup), upgrade_fee: v(r.up), downgrade_fee: v(r.down), percent: null };
    });
  const unreadable = typed.some((r) => [r.amount, r.setup_fee, r.upgrade_fee, r.downgrade_fee].includes(null));
  const badPercent = typed.some((r) => r.cycle === 'H1' && r.percent === null);
  const w = useWrite((b: Parameters<typeof api.publishOptionPrices>[1], opts) => (option ? api.publishOptionPrices(target, b, opts) : api.publishAddonPrices(target, b, opts)));
  const set = (c: CycleCode, k: keyof typeof blank, v: string) => setRows({ ...rows, [c]: { ...(rows[c] ?? blank), [k]: v } });
  return (
    <FormShell
      write={w}
      close={close}
      disabled={!book || typed.length === 0 || unreadable || badPercent}
      onSubmit={(reason) =>
        void w
          .run({
            price_book_id: book,
            prices: typed.map((r) =>
              option
                ? { cycle: r.cycle, amount: r.amount ?? '0', setup_fee: r.setup_fee ?? '0', upgrade_fee: r.upgrade_fee ?? '0', downgrade_fee: r.downgrade_fee ?? '0', percent: r.percent }
                : { cycle: r.cycle, amount: r.amount ?? '0', setup_fee: r.setup_fee ?? '0' },
            ),
            effective_from: iso(from),
            reason,
          })
          .then((r) => r && changed())
      }
    >
      {quantity ? <p className="text-sm text-muted-foreground">{t('editor.perUnit')}</p> : null}
      <CatalogField name="priceBook">
        <select className={selectClass} value={book} onChange={(e) => setBook(e.target.value)}>
          {books.map((b) => (
            <option key={b.id} value={b.id}>
              {b.code} · {t(`prices.bookKind.${b.applies_to}`)}
            </option>
          ))}
        </select>
      </CatalogField>
      <div className="grid gap-3">
        {sortCycles(cycles).map((c) => {
          const now = current.find((r) => r.price_book_id === book && r.cycle === c && r.effective_until === null);
          if (c === 'H1') {
            return (
              <div key={c} className="grid items-end gap-2 sm:grid-cols-[7rem_1fr]">
                <span className="pb-2 text-sm font-medium">
                  {t('prices.hourly')}
                  {now?.percent ? <span className="block text-xs font-normal text-muted-foreground">{t('options.percent', { pct: Number(now.percent) })}</span> : null}
                </span>
                <CatalogField name="pricePercent">
                  <Input inputMode="decimal" value={rows[c]?.pct ?? ''} onChange={(e) => set(c, 'pct', e.target.value)} />
                </CatalogField>
              </div>
            );
          }
          return (
            <div key={c} className={`grid items-end gap-2 ${option ? 'sm:grid-cols-[7rem_1fr_1fr_1fr_1fr]' : 'sm:grid-cols-[7rem_1fr_1fr]'}`}>
              <span className="pb-2 text-sm font-medium">
                {t(`cycle.${c}`)}
                {now ? <span className="block text-xs font-normal text-muted-foreground">{t('options.price', { amount: formatVnd(now.amount), cycle: t(`cycle.${c}`) })}</span> : null}
              </span>
              <CatalogField name="priceAmount">
                <Input inputMode="numeric" value={rows[c]?.amount ?? ''} onChange={(e) => set(c, 'amount', e.target.value)} />
              </CatalogField>
              <CatalogField name="setupFee">
                <Input inputMode="numeric" value={rows[c]?.setup ?? ''} onChange={(e) => set(c, 'setup', e.target.value)} placeholder="0" />
              </CatalogField>
              {option ? (
                <>
                  <CatalogField name="upgradeFee">
                    <Input inputMode="numeric" value={rows[c]?.up ?? ''} onChange={(e) => set(c, 'up', e.target.value)} placeholder="0" />
                  </CatalogField>
                  <CatalogField name="downgradeFee">
                    <Input inputMode="numeric" value={rows[c]?.down ?? ''} onChange={(e) => set(c, 'down', e.target.value)} placeholder="0" />
                  </CatalogField>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
      {unreadable ? <Callout tone="warn">{t('editor.unreadable')}</Callout> : null}
      {badPercent ? <Callout tone="warn">{t('editor.unreadablePercent')}</Callout> : null}
      <CatalogField name="priceFrom">
        <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
      </CatalogField>
    </FormShell>
  );
}

// ---------------------------------------------------------------------------------------------
// Add-ons

export function AddonForm({ close, changed, addon, defaultProduct }: DialogBodyProps & { addon?: CatalogAddon; defaultProduct?: string }) {
  const { t, name } = useCatalogT();
  const products = useApi((signal) => api.listCatalogProducts(null, { signal }), []);
  const { code, type: setCode, fromName } = useSuggestedCode(addon?.code, 60);
  const [vi, setVi] = useState(addon?.name_vi ?? '');
  const [enName, setEn] = useState(addon?.name_en ?? '');
  const [billing, setBilling] = useState<CatalogAddon['billing']>(addon?.billing ?? 'RECURRING');
  const [fulfilment, setFulfilment] = useState<CatalogAddon['fulfilment']>(addon?.fulfilment ?? 'MANUAL');
  const [action, setAction] = useState(addon?.module_op ?? '');
  const [onProducts, setOnProducts] = useState<string[]>(addon?.products ?? (defaultProduct ? [defaultProduct] : []));
  const [tax, setTax] = useState(addon?.tax_code ?? '');
  const [visibility, setVisibility] = useState<Visibility>(addon?.visibility ?? 'DRAFT');
  type Body = Parameters<typeof api.createCatalogAddon>[0];
  const w = useWrite((b: Body, opts) => (addon ? api.updateCatalogAddon(addon.id, b, opts) : api.createCatalogAddon(b, opts)));
  const valid = Boolean(addon) || codeOk(code);
  const auto = fulfilment === 'MODULE_OP';
  const list = products.status === 'success' ? products.data.products : [];
  return (
    <FormShell
      write={w}
      close={close}
      disabled={!valid || !vi.trim() || (auto && !action.trim())}
      onSubmit={(reason) =>
        void w
          .run({ code: addon ? undefined : code, version: addon?.version, name_vi: vi.trim(), name_en: (enName || vi).trim(), billing, fulfilment, module_op: auto ? action.trim() : null, products: onProducts, tax_code: tax, visibility, reason })
          .then((r) => r && changed())
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <NameFields vi={vi} en={enName} setVi={(v) => { setVi(v); fromName(v); }} setEn={setEn} />
        <CatalogField name="code">
          <CodeInput value={code} onChange={setCode} locked={Boolean(addon)} valid={valid} />
        </CatalogField>
        <CatalogField name="addonBilling">
          <select className={selectClass} value={billing} onChange={(e) => setBilling(e.target.value as CatalogAddon['billing'])}>
            {ADDON_BILLINGS.map((b) => (
              <option key={b} value={b}>
                {t(`addons.billing.${b}`)}
              </option>
            ))}
          </select>
        </CatalogField>
        <CatalogField name="addonFulfilment">
          <select className={selectClass} value={fulfilment} onChange={(e) => setFulfilment(e.target.value as CatalogAddon['fulfilment'])}>
            {ADDON_FULFILMENTS.map((f) => (
              <option key={f} value={f}>
                {t(`editor.addonFulfilment.${f}`)}
              </option>
            ))}
          </select>
        </CatalogField>
        {auto ? (
          <CatalogField name="addonAction">
            <Input value={action} onChange={(e) => setAction(e.target.value)} className="font-mono" required />
          </CatalogField>
        ) : null}
        <CatalogField name="taxCode">
          <TaxCodeSelect value={tax} onChange={setTax} current={addon?.tax_code} />
        </CatalogField>
        <CatalogField name="addonVisibility">
          <select className={selectClass} value={visibility} onChange={(e) => setVisibility(e.target.value as Visibility)}>
            {VISIBILITIES.map((v) => (
              <option key={v} value={v}>
                {t(`visibility.${v}`)}
              </option>
            ))}
          </select>
        </CatalogField>
      </div>
      {list.length ? <CheckList name="addonProducts" options={list.map((p) => p.id)} value={onProducts} onChange={setOnProducts} label={(id) => name(list.find((p) => p.id === id) ?? { name_vi: id, name_en: id })} /> : null}
    </FormShell>
  );
}

// ---------------------------------------------------------------------------------------------
// A plan's cross-sells, free domains, prices and locations

export function CrossSellForm({ close, changed, d }: DialogBodyProps & { d: PlanDetail }) {
  const { t, en } = useCatalogT();
  const all = useApi((signal) => api.listCatalogPlans(null, { signal }), []);
  const [chosen, setChosen] = useState<string[]>(d.cross_sells.map((c) => c.offered_plan_id));
  const w = useWrite((b: { offered: { plan_id: string; sort: number }[]; reason: string }, opts) => api.setCrossSells(d.plan.id, b, opts));
  const others = all.status === 'success' ? all.data.plans.filter((p) => p.id !== d.plan.id && p.product_id !== d.product.id) : [];
  const label = (id: string) => {
    const p = others.find((x) => x.id === id);
    return p ? `${en ? p.product_name_en : p.product_name_vi} · ${en ? p.name_en : p.name_vi}` : id;
  };
  return (
    <FormShell write={w} close={close} onSubmit={(reason) => void w.run({ offered: chosen.map((plan_id, i) => ({ plan_id, sort: (i + 1) * 10 })), reason }).then((r) => r && changed())}>
      <p className="text-sm text-muted-foreground">{t('alongside.intro')}</p>
      {others.length ? <CheckList name="crossSellPlans" options={others.map((p) => p.id)} value={chosen} onChange={setChosen} label={label} /> : null}
    </FormShell>
  );
}

export function FreeDomainsForm({ close, changed, d }: DialogBodyProps & { d: PlanDetail }) {
  const { t } = useCatalogT();
  // ADR-032 §2.3: no free domain for a one-time or an hourly plan.
  const recurring = sortCycles(d.plan.cycles).filter((c) => c !== 'ONCE' && c !== 'H1');
  const [rows, setRows] = useState<FreeDomainInput[]>(d.free_domains.map((f) => ({ tld_id: f.tld_id, min_cycle: f.min_cycle, operations: f.operations, free_renewals: f.free_renewals })));
  const w = useWrite((b: { domains: FreeDomainInput[]; reason: string }, opts) => api.setFreeDomains(d.plan.id, b, opts));
  const set = (i: number, patch: Partial<FreeDomainInput>) => setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const free = d.tlds.filter((x) => !rows.some((r) => r.tld_id === x.id));
  const incomplete = rows.some((r) => !r.tld_id || r.operations.length === 0);
  return (
    <FormShell write={w} close={close} disabled={incomplete} onSubmit={(reason) => void w.run({ domains: rows, reason }).then((r) => r && changed())}>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">{t('domains.none')}</p> : null}
      {rows.map((r, i) => (
        <div key={i} className="grid gap-2 rounded-md border p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <CatalogField name="freeZone">
              <select className={selectClass} value={r.tld_id} onChange={(e) => set(i, { tld_id: e.target.value })}>
                {d.tlds
                  .filter((x) => x.id === r.tld_id || !rows.some((o) => o.tld_id === x.id))
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      .{x.zone}
                    </option>
                  ))}
              </select>
            </CatalogField>
            <CatalogField name="freeMinCycle">
              <select className={selectClass} value={r.min_cycle} onChange={(e) => set(i, { min_cycle: e.target.value as CycleCode })}>
                {recurring.map((c) => (
                  <option key={c} value={c}>
                    {t(`cycle.${c}`)}
                  </option>
                ))}
              </select>
            </CatalogField>
          </div>
          <CheckList name="freeOperations" options={FREE_DOMAIN_OPERATIONS} value={r.operations} onChange={(v) => set(i, { operations: v })} label={(o) => t(`editor.freeOperation.${o}`)} />
          <CatalogCheck name="freeRenewals" checked={r.free_renewals} onChange={(v) => set(i, { free_renewals: v })} />
          <Button type="button" size="sm" variant="ghost" className="w-fit" onClick={() => setRows(rows.filter((_, k) => k !== i))}>
            <Trash2 className="size-4" aria-hidden="true" />
            {t('editor.remove')}
          </Button>
        </div>
      ))}
      {free.length && recurring.length ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="w-fit"
          onClick={() => setRows([...rows, { tld_id: free[0]?.id ?? '', min_cycle: recurring.includes('M12') ? 'M12' : (recurring[0] ?? 'M12'), operations: ['REGISTER'], free_renewals: false }])}
        >
          <Plus className="size-4" aria-hidden="true" />
          {t('editor.addFreeDomain')}
        </Button>
      ) : null}
    </FormShell>
  );
}

/** Ends one of the plan's price windows (`pricing.manage`). */
export function EndPlanPriceForm({ close, changed, d }: DialogBodyProps & { d: PlanDetail }) {
  const { t } = useCatalogT();
  const open = d.prices.filter((r) => r.effective_until === null);
  const [price, setPrice] = useState(open[0]?.id ?? '');
  const [at, setAt] = useState('');
  const w = useWrite((b: { effective_until: string | null; reason: string }, opts) => api.endPlanPrice(price, b, opts));
  if (open.length === 0) return <Callout tone="info">{t('editor.endPriceNone')}</Callout>;
  return (
    <FormShell write={w} close={close} disabled={!price} onSubmit={(reason) => void w.run({ effective_until: iso(at), reason }).then((r) => r && changed())}>
      <CatalogField name="endPrice">
        <select className={selectClass} value={price} onChange={(e) => setPrice(e.target.value)}>
          {open.map((r) => (
            <option key={r.id} value={r.id}>
              {t('editor.endPriceRow', { book: r.price_book_code, cycle: t(`cycle.${r.cycle}`), amount: formatVnd(r.amount) })} · {formatDateTime(r.effective_from)}
            </option>
          ))}
        </select>
      </CatalogField>
      <CatalogField name="endAt">
        <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
      </CatalogField>
    </FormShell>
  );
}

/** Sells the plan at a location where it has no stock row yet. */
export function AddStockForm({ close, changed, d }: DialogBodyProps & { d: PlanDetail }) {
  const { t, name } = useCatalogT();
  const free = d.locations.filter((l) => l.active && !d.stock.some((s) => s.location_code === l.code));
  const [location, setLocation] = useState(free[0]?.code ?? '');
  const [mode, setMode] = useState<StockMode>('UNLIMITED');
  const [qty, setQty] = useState('');
  const w = useWrite((b: { reason: string }, opts) => api.setPlanStock(d.plan.id, location, { version: null, mode, quantity: mode === 'COUNTED' ? Number(qty || '0') : null, reason: b.reason }, opts));
  return (
    <FormShell write={w} close={close} disabled={!location} onSubmit={(reason) => void w.run({ reason }).then((r) => r && changed())}>
      <CatalogField name="stockLocation">
        <select className={selectClass} value={location} onChange={(e) => setLocation(e.target.value)}>
          {free.map((l) => (
            <option key={l.code} value={l.code}>
              {name(l)} · {l.city}
            </option>
          ))}
        </select>
      </CatalogField>
      <CatalogField name="stockMode">
        <select className={selectClass} value={mode} onChange={(e) => setMode(e.target.value as StockMode)}>
          {STOCK_MODES.map((m) => (
            <option key={m} value={m}>
              {t(`stock.mode.${m}`)}
            </option>
          ))}
        </select>
      </CatalogField>
      {mode === 'COUNTED' ? (
        <CatalogField name="stockQuantity">
          <Input type="number" min={0} value={qty} onChange={(e) => setQty(e.target.value)} className="w-32" />
        </CatalogField>
      ) : null}
    </FormShell>
  );
}

/** The product follows its group's overdue settings again (`catalog.manage`). */
export function ClearLifecycleForm({ close, changed, product }: DialogBodyProps & { product: CatalogProduct }) {
  const { t } = useCatalogT();
  const w = useWrite((b: { reason: string }, opts) => api.clearLifecycle({ scope: 'PRODUCT', scope_ref: product.id, reason: b.reason }, opts));
  return (
    <FormShell write={w} close={close} onSubmit={(reason) => void w.run({ reason }).then((r) => r && changed())}>
      <p className="text-sm text-muted-foreground">{t('editor.clearLifecycleIntro')}</p>
    </FormShell>
  );
}

// ---------------------------------------------------------------------------------------------
// Locations and server groups

export function LocationEditForm({ close, changed, location }: DialogBodyProps & { location: Location }) {
  const [vi, setVi] = useState(location.name_vi);
  const [enName, setEn] = useState(location.name_en);
  const [city, setCity] = useState(location.city);
  const [active, setActive] = useState(location.active);
  const w = useWrite((b: Parameters<typeof api.updateLocation>[1], opts) => api.updateLocation(location.code, b, opts));
  return (
    <FormShell
      write={w}
      close={close}
      disabled={!vi.trim() || !city.trim()}
      onSubmit={(reason) => void w.run({ version: location.version, name_vi: vi.trim(), name_en: (enName || vi).trim(), city: city.trim(), active, reason }).then((r) => r && changed())}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <NameFields vi={vi} en={enName} setVi={setVi} setEn={setEn} />
        <CatalogField name="locationCity">
          <Input value={city} onChange={(e) => setCity(e.target.value)} required />
        </CatalogField>
      </div>
      <CatalogCheck name="locationActive" checked={active} onChange={setActive} />
    </FormShell>
  );
}

export function PlacementGroupForm({ close, changed, group, locations }: DialogBodyProps & { group?: PlacementGroup; locations: readonly Location[] }) {
  const { t, name } = useCatalogT();
  const { code, type: setCode, fromName } = useSuggestedCode(group?.code, 40);
  const [vi, setVi] = useState(group?.name_vi ?? '');
  const [enName, setEn] = useState(group?.name_en ?? '');
  const [branch, setBranch] = useState<Branch>(group?.branch ?? 'hosting');
  const [strategy, setStrategy] = useState<PlacementGroup['strategy']>(group?.strategy ?? 'LEAST_USED');
  const [location, setLocation] = useState(group?.location_code ?? '');
  const text = useText();
  // ADR-032 §2.4: a group serves one module, fixed once it has members or plans; a fixed module is
  // not sent, so the stored one stays (§4.2).
  const [module, setModule] = useState(group?.module ?? '');
  const moduleLocked = Boolean(group && (group.members.length > 0 || (group.plan_count ?? 0) > 0));
  const modules = useModuleSummaries(branch);
  const manifest = modules?.find((m) => m.manifest.id === module)?.manifest ?? null;
  const fields = manifest?.placement_settings.fields ?? [];
  // §2.5: the defaults of the module's placement settings; typed values over the stored ones.
  const [typed, setTyped] = useState<Record<string, FormValue>>({});
  const values = { ...placementValues(fields, module === group?.module ? group.module_settings : {}, false), ...typed };
  const settings = placementPayload(fields, values, false);
  type Body = Parameters<typeof api.createPlacementGroup>[0];
  const w = useWrite((b: Body, opts) => (group ? api.updatePlacementGroup(group.code, b, opts) : api.createPlacementGroup(b, opts)));
  const valid = Boolean(group) || codeOk(code, 40);
  return (
    <FormShell
      write={w}
      close={close}
      disabled={!valid || !vi.trim() || !module || !settings.valid}
      onSubmit={(reason) =>
        void w
          .run({
            code: group ? undefined : code,
            version: group?.version,
            branch,
            name_vi: vi.trim(),
            name_en: (enName || vi).trim(),
            strategy,
            location_code: location || null,
            module: moduleLocked ? undefined : module,
            // Without the module's manifest (no `integration.read`) the stored defaults stay.
            module_settings: manifest ? settings.payload : undefined,
            reason,
          })
          .then((r) => r && changed())
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <NameFields vi={vi} en={enName} setVi={(v) => { setVi(v); fromName(v); }} setEn={setEn} />
        <CatalogField name="code">
          <CodeInput value={code} onChange={setCode} locked={Boolean(group)} valid={valid} />
        </CatalogField>
        <CatalogField name="serverBranch">
          <select
            className={selectClass}
            value={branch}
            disabled={moduleLocked}
            onChange={(e) => {
              setBranch(e.target.value as Branch);
              setModule('');
              setTyped({});
            }}
          >
            {BRANCHES.map((b) => (
              <option key={b} value={b}>
                {t(`branch.${b}`)}
              </option>
            ))}
          </select>
        </CatalogField>
        <CatalogField name="serverStrategy">
          <select className={selectClass} value={strategy} onChange={(e) => setStrategy(e.target.value as PlacementGroup['strategy'])}>
            {STRATEGIES.map((s) => (
              <option key={s} value={s}>
                {t(`servers.strategy.${s}`)}
              </option>
            ))}
          </select>
        </CatalogField>
        <CatalogField name="serverLocation">
          <select className={selectClass} value={location} onChange={(e) => setLocation(e.target.value)}>
            <option value="">{t('editor.none')}</option>
            {locations.map((l) => (
              <option key={l.code} value={l.code}>
                {name(l)}
              </option>
            ))}
          </select>
        </CatalogField>
        <CatalogField name="serverModule">
          {modules ? (
            <select
              className={selectClass}
              value={module}
              disabled={moduleLocked}
              onChange={(e) => {
                setModule(e.target.value);
                setTyped({});
              }}
              required
            >
              <option value="">{t('order.choose')}</option>
              {module && !modules.some((m) => m.manifest.id === module) ? <option value={module}>{module}</option> : null}
              {modules.map((m) => (
                <option key={m.manifest.id} value={m.manifest.id}>
                  {text(m.manifest.name)}
                </option>
              ))}
            </select>
          ) : (
            <Input value={module} onChange={(e) => setModule(e.target.value.trim().toLowerCase())} readOnly={moduleLocked} className="font-mono" required />
          )}
          {moduleLocked ? <span className="text-xs text-muted-foreground">{t('servers.moduleLocked')}</span> : null}
        </CatalogField>
      </div>
      {fields.length ? (
        <fieldset className="grid gap-3 rounded-md border p-3">
          <legend className="px-1 text-sm font-medium">{t('servers.defaults')}</legend>
          <p className="text-xs text-muted-foreground">{t('servers.defaultsHint')}</p>
          <PlacementFields
            fields={fields}
            values={values}
            onChange={(k, v) => setTyped((x) => ({ ...x, [k]: v }))}
            list={{ branch, module, keys: memberKeys(group?.members ?? [], module, true), facts: null }}
          />
        </fieldset>
      ) : null}
    </FormShell>
  );
}
